import { useEffect,useMemo,useRef,useState } from 'react';
import { useParams } from 'react-router-dom';
import {
IconPlus,
IconEye,
IconCircleCheck,
IconAlertCircle,
IconTrash,
IconMail,
IconLoader2,
} from '@tabler/icons-react';
import api from '../lib/api';
import { GOOGLE_MAPS_API_KEY } from '../env';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert,AlertDescription } from '@/components/ui/alert';
import {
Dialog,
DialogContent,
DialogHeader,
DialogFooter,
DialogTitle,
DialogDescription,
} from '@/components/ui/dialog';
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

type RoleType = { roleTypeId: number; roleName: string; description: string | null };

function normalizeRoleName(roleName: string): string {
  return roleName?.toLowerCase().replace(/\s+/g, '') || '';
}

function findRoleByName(roleTypes: RoleType[], searchName: string): RoleType | undefined {
  const normalized = normalizeRoleName(searchName);
  return roleTypes.find(r => normalizeRoleName(r.roleName) === normalized);
}

const COUNTRIES = [
  { code: 'ZAF', name: 'South Africa' },
  { code: 'USA', name: 'United States' },
  { code: 'GBR', name: 'United Kingdom' },
  { code: 'CAN', name: 'Canada' },
  { code: 'AUS', name: 'Australia' },
  { code: 'NZL', name: 'New Zealand' },
  { code: 'DEU', name: 'Germany' },
  { code: 'FRA', name: 'France' },
  { code: 'ITA', name: 'Italy' },
  { code: 'ESP', name: 'Spain' },
  { code: 'NLD', name: 'Netherlands' },
  { code: 'BEL', name: 'Belgium' },
  { code: 'CHE', name: 'Switzerland' },
  { code: 'AUT', name: 'Austria' },
  { code: 'SWE', name: 'Sweden' },
  { code: 'NOR', name: 'Norway' },
  { code: 'DNK', name: 'Denmark' },
  { code: 'FIN', name: 'Finland' },
  { code: 'POL', name: 'Poland' },
  { code: 'PRT', name: 'Portugal' },
  { code: 'GRC', name: 'Greece' },
  { code: 'IRL', name: 'Ireland' },
  { code: 'CZE', name: 'Czech Republic' },
  { code: 'HUN', name: 'Hungary' },
  { code: 'ROU', name: 'Romania' },
  { code: 'BGR', name: 'Bulgaria' },
  { code: 'HRV', name: 'Croatia' },
  { code: 'SVK', name: 'Slovakia' },
  { code: 'SVN', name: 'Slovenia' },
  { code: 'EST', name: 'Estonia' },
  { code: 'LVA', name: 'Latvia' },
  { code: 'LTU', name: 'Lithuania' },
  { code: 'LUX', name: 'Luxembourg' },
  { code: 'MLT', name: 'Malta' },
  { code: 'CYP', name: 'Cyprus' },
  { code: 'JPN', name: 'Japan' },
  { code: 'CHN', name: 'China' },
  { code: 'IND', name: 'India' },
  { code: 'KOR', name: 'South Korea' },
  { code: 'SGP', name: 'Singapore' },
  { code: 'MYS', name: 'Malaysia' },
  { code: 'THA', name: 'Thailand' },
  { code: 'IDN', name: 'Indonesia' },
  { code: 'PHL', name: 'Philippines' },
  { code: 'VNM', name: 'Vietnam' },
  { code: 'BRA', name: 'Brazil' },
  { code: 'ARG', name: 'Argentina' },
  { code: 'CHL', name: 'Chile' },
  { code: 'COL', name: 'Colombia' },
  { code: 'MEX', name: 'Mexico' },
  { code: 'PER', name: 'Peru' },
  { code: 'VEN', name: 'Venezuela' },
  { code: 'EGY', name: 'Egypt' },
  { code: 'ZWE', name: 'Zimbabwe' },
  { code: 'BWA', name: 'Botswana' },
  { code: 'NAM', name: 'Namibia' },
  { code: 'MOZ', name: 'Mozambique' },
  { code: 'AGO', name: 'Angola' },
  { code: 'KEN', name: 'Kenya' },
  { code: 'TZA', name: 'Tanzania' },
  { code: 'UGA', name: 'Uganda' },
  { code: 'GHA', name: 'Ghana' },
  { code: 'NGA', name: 'Nigeria' },
  { code: 'RUS', name: 'Russia' },
  { code: 'TUR', name: 'Turkey' },
  { code: 'SAU', name: 'Saudi Arabia' },
  { code: 'ARE', name: 'United Arab Emirates' },
  { code: 'ISR', name: 'Israel' },
  { code: 'NZL', name: 'New Zealand' },
].sort((a, b) => a.name.localeCompare(b.name));

function extractDateOfBirthFromId(idNumber: string): string | null {
  if (!idNumber || idNumber.length < 6) return null;
  const dateStr = idNumber.substring(0, 6);
  if (!/^\d{6}$/.test(dateStr)) return null;
  const year = parseInt(dateStr.substring(0, 2), 10);
  const month = parseInt(dateStr.substring(2, 4), 10);
  const day = parseInt(dateStr.substring(4, 6), 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const fullYear = year <= 30 ? 2000 + year : 1900 + year;
  const date = new Date(fullYear, month - 1, day);
  if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function validatePhoneNumber(phone: string, countryCode: string = 'ZAF'): boolean {
  if (!phone || !phone.trim()) return true;
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  if (countryCode === 'ZAF') {
    if (cleaned.startsWith('+27')) return /^\+27\d{9}$/.test(cleaned);
    if (cleaned.startsWith('27')) return /^27\d{9}$/.test(cleaned);
    if (cleaned.startsWith('0')) return /^0\d{9}$/.test(cleaned);
    return /^\d{9,10}$/.test(cleaned);
  }
  return /^\+?\d{7,15}$/.test(cleaned);
}

export function LegalEntityDetailPage() {
  const { entityId: entityUuid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entity, setEntity] = useState<any>(null);
  const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
  const [personLinks, setPersonLinks] = useState<any[]>([]);
  const [entityLinks, setEntityLinks] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docForm, setDocForm] = useState({
    document_type: '',
    file_name: '',
    file_path: '',
    issue_date: '',
    expiry_date: '',
  });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docUploading, setDocUploading] = useState(false);
  const [viewDocModalOpen, setViewDocModalOpen] = useState(false);
  const [viewDocUrl, setViewDocUrl] = useState<string | null>(null);
  const [viewDocLoading, setViewDocLoading] = useState(false);
  const [viewDocError, setViewDocError] = useState<string | null>(null);
  const [viewingDocId, setViewingDocId] = useState<number | null>(null);
  const [viewPersonModalOpen, setViewPersonModalOpen] = useState(false);
  const [viewingPerson, setViewingPerson] = useState<any>(null);
  const [verificationHistory, setVerificationHistory] = useState<any[]>([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImageTitle, setSelectedImageTitle] = useState<string>('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [addrModalOpen, setAddrModalOpen] = useState<{ type: 'registered' | 'business' | null }>({ type: null });
  const [addrDetailsLoading, setAddrDetailsLoading] = useState(false);
  const gmapsAutocompleteInputRef = useRef<HTMLInputElement | null>(null);
  const gmapsAutocompleteRef = useRef<any>(null);
  const gmapsPlaceElementRef = useRef<any>(null);
  const [gmapsReady, setGmapsReady] = useState<boolean>(false);
  const [gmapsUnavailable, setGmapsUnavailable] = useState<boolean>(false);
  const [gmapsInitTried, setGmapsInitTried] = useState<boolean>(false);
  const [gmapsExtendedReady, setGmapsExtendedReady] = useState<boolean>(false);
  const [gmapsPreds, setGmapsPreds] = useState<any[]>([]);
  const [gmapsPredLoading, setGmapsPredLoading] = useState<boolean>(false);
  const [addrForm, setAddrForm] = useState({
    line1: '',
    line2: '',
    suburb: '',
    city: '',
    postalCode: '',
    province: '',
    country: 'ZAF',
  });
  const [personQuery, setPersonQuery] = useState('');
  const [personOptions, setPersonOptions] = useState<any[]>([]);
  const [personLoading, setPersonLoading] = useState(false);
  const personDebounceRef = useRef<number | null>(null);
  const [entityQuery, setEntityQuery] = useState('');
  const [entityOptions, setEntityOptions] = useState<any[]>([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const entityDebounceRef = useRef<number | null>(null);

  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [personIdentityType, setPersonIdentityType] = useState<'id_number' | 'passport'>('id_number');
  const [personForm, setPersonForm] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    passportNumber: '',
    passportCountry: 'ZAF',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    country: 'ZAF',
  });
  const [phoneError, setPhoneError] = useState<string>('');
  const [personOwnershipPercentage, setPersonOwnershipPercentage] = useState<string>('');
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [personSearching, setPersonSearching] = useState(false);
  const [personFound, setPersonFound] = useState<boolean | null>(null);
  const [sendLinkModalOpen, setSendLinkModalOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [showPersonDetails, setShowPersonDetails] = useState(false);

  const [ownerEntityModalOpen, setOwnerEntityModalOpen] = useState(false);
  const [ownerPercentage, setOwnerPercentage] = useState<string>('');
  const [ownerEntityForm, setOwnerEntityForm] = useState({
    entity_type: 'Company' as 'Company' | 'Trust',
    company_category: '',
    registered_name: '',
    registration_number: '',
    trading_name: '',
    income_tax_number: '',
    vat_number: '',
  });
  const [ownerEntityCreating, setOwnerEntityCreating] = useState(false);
  const [ownerEntityError, setOwnerEntityError] = useState<string | null>(null);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [ownerType, setOwnerType] = useState<'Person' | 'Entity'>('Person');
  const [ownerPersonId, setOwnerPersonId] = useState<number | null>(null);
  const [ownerEntityId, setOwnerEntityId] = useState<number | null>(null);

  const entityTitle = useMemo(() => entity?.registeredName || 'Legal Entity', [entity]);

  const [basicEdits, setBasicEdits] = useState<{ entity_type: 'Company' | 'Trust'; company_category: string | null }>({
    entity_type: 'Company',
    company_category: null,
  });
  useEffect(() => {
    if (entity) {
      setBasicEdits({
        entity_type: entity.entityType as 'Company' | 'Trust',
        company_category: entity.companyCategory ?? null,
      });
    }
  }, [entity]);

  const saveBasics = async (next: { entity_type?: 'Company' | 'Trust'; company_category?: string | null }) => {
    if (!entityUuid) return;
    try {
      setBasicEdits((prev) => ({ ...prev, ...next }));
      await api.patch(`/legal-entities/${entityUuid}`, {
        entity_type: next.entity_type ?? basicEdits.entity_type,
        company_category:
          (next.entity_type ?? basicEdits.entity_type) === 'Company'
            ? (next.company_category ?? basicEdits.company_category ?? null)
            : null,
      });
      await load();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to update entity');
    }
  };

  const load = async () => {
    if (!entityUuid) return;
    setLoading(true);
    setError(null);
    try {
      const [eRes, rRes, pplRes, eelRes, docsRes] = await Promise.all([
        api.get(`/legal-entities/${entityUuid}`),
        api.get('/role-types'),
        api.get(`/entity-links/person/${entityUuid}`),
        api.get(`/entity-links/entity/${entityUuid}`),
        api.get(`/verification-documents`, { params: { entity_uuid: entityUuid } }),
      ]);
      setEntity(eRes.data?.data ?? null);
      const roles = rRes.data?.data ?? [];
      setRoleTypes(roles);
      if (roles.length === 0) {
        console.warn('No role types found in database. Please run the seed script: mysql-init/17-seed-role-types.sql');
      }
      setPersonLinks(pplRes.data?.data ?? []);
      setEntityLinks(eelRes.data?.data ?? []);
      setDocuments(docsRes.data?.data ?? []);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load entity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityUuid]);

  useEffect(() => {
    if (viewPersonModalOpen && viewingPerson?.personId) {
      const loadVerificationHistory = async () => {
        try {
          const res = await api.get(`/natural-persons/${viewingPerson.personId}/verification-history`);
          setVerificationHistory(res.data?.data || []);
        } catch (err) {
          console.error('Failed to load verification history:', err);
        }
      };
      loadVerificationHistory();
    }
  }, [viewPersonModalOpen, viewingPerson?.personId]);

  const handleSearchPerson = async () => {
    if (!entityUuid) return;
    setPersonSearching(true);
    setPersonFound(null);
    try {
      const params: any = {};
      if (personIdentityType === 'id_number') {
        if (!personForm.idNumber.trim()) {
          alert('Please enter an ID Number');
          setPersonSearching(false);
          return;
        }
        params.id_number = personForm.idNumber.trim();
      } else {
        if (!personForm.passportNumber.trim() || !personForm.passportCountry.trim()) {
          alert('Please enter both Passport Number and Country');
          setPersonSearching(false);
          return;
        }
        params.passport_number = personForm.passportNumber.trim();
        params.passport_country = personForm.passportCountry.trim();
      }

      const res = await api.get('/natural-persons/find-by-identity', { params });
      if (res.data?.found && res.data?.data) {
        const person = res.data.data;
        setPersonFound(true);
        setSelectedPersonId(person.personId);
        setPersonForm({
          firstName: person.firstName || '',
          lastName: person.lastName || '',
          idNumber: person.idNumber || '',
          passportNumber: person.passportNumber || '',
          passportCountry: person.passportCountry || 'ZAF',
          email: person.email || '',
          phoneNumber: person.phoneNumber || '',
          dateOfBirth: person.dateOfBirth ? new Date(person.dateOfBirth).toISOString().split('T')[0] : '',
          country: 'ZAF',
        });
        setShowPersonDetails(false);
      } else {
        setPersonFound(false);
        setShowPersonDetails(true);
      }
    } catch (err: any) {
      console.error(err);
      if (err.response?.status === 404 || err.response?.data?.found === false) {
        setPersonFound(false);
        setShowPersonDetails(true);
      } else {
        alert(err.response?.data?.message || 'Failed to search for person');
      }
    } finally {
      setPersonSearching(false);
    }
  };

  const handleCreatePersonAndLink = async () => {
    if (!selectedRoleId || !entityUuid) {
      console.error('Missing selectedRoleId or entityUuid:', { selectedRoleId, entityUuid });
      alert('Role is required. Please close and try again.');
      return;
    }

    if (personFound === true && selectedPersonId) {
      // Person exists, just link them
    } else if (personFound === false) {
      if (!personForm.firstName.trim() || !personForm.lastName.trim()) {
        alert('First Name and Last Name are required when creating a new person.');
        return;
      }
      if (personIdentityType === 'id_number' && !personForm.idNumber.trim()) {
        alert('ID Number is required.');
        return;
      }
      if (personIdentityType === 'passport' && (!personForm.passportNumber.trim() || !personForm.passportCountry.trim())) {
        alert('Passport Number and Country are required.');
        return;
      }
    } else if (personFound === null) {
      alert('Please click "Search" to check if the person exists in the database.');
      return;
    }

    try {
      let personId = selectedPersonId;
      if (!personId) {
        const createPayload: any = {
          firstName: personForm.firstName.trim(),
          lastName: personForm.lastName.trim(),
          email: personForm.email.trim() || undefined,
          phoneNumber: personForm.phoneNumber.trim() || undefined,
          dateOfBirth: personForm.dateOfBirth || undefined,
        };

        if (personIdentityType === 'id_number') {
          createPayload.idNumber = personForm.idNumber.trim();
        } else {
          createPayload.passportNumber = personForm.passportNumber.trim();
          createPayload.passportCountry = personForm.passportCountry.trim().toUpperCase();
        }

        const create = await api.post('/natural-persons', createPayload);
        personId = create.data?.data?.personId;
      }
      const isBeneficialOwner = roleTypes.find(r => normalizeRoleName(r.roleName) === normalizeRoleName('BeneficialOwner_5Percent'))?.roleTypeId === selectedRoleId;
      await api.post('/entity-links/person', {
        entity_uuid: entityUuid,
        person_id: personId,
        role_type_id: selectedRoleId,
        ownership_percentage: isBeneficialOwner && personOwnershipPercentage ? personOwnershipPercentage : undefined,
      });
      setPersonModalOpen(false);
      setSelectedPersonId(null);
      setSelectedRoleId('');
      setPersonIdentityType('id_number');
      setPersonForm({ firstName: '', lastName: '', idNumber: '', passportNumber: '', passportCountry: 'ZAF', email: '', phoneNumber: '', dateOfBirth: '', country: 'ZAF' });
      setPhoneError('');
      setPersonFound(null);
      setShowPersonDetails(false);
      await load();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to add person link');
    }
  };

  const handleAddOwner = async () => {
    if (!entityUuid) return;
    try {
      if (ownerType === 'Person') {
        if (!ownerPersonId) return;
        await api.post('/entity-links/person', {
          entity_uuid: entityUuid,
          person_id: ownerPersonId,
          role_type_id: findRoleByName(roleTypes, 'BeneficialOwner_5Percent')?.roleTypeId,
          ownership_percentage: ownerPercentage || undefined,
        });
      } else {
        if (!ownerEntityId || !ownerPercentage) return;
        await api.post('/entity-links/entity', {
          parent_entity_uuid: entityUuid,
          child_entity_id: ownerEntityId,
          ownership_percentage: ownerPercentage,
        });
        alert(
          'Ownership link created. Note: The linked entity will be pending further FICA (add its directors/owners/documents).',
        );
      }
      setOwnerModalOpen(false);
      setOwnerType('Person');
      setOwnerPercentage('');
      setOwnerPersonId(null);
      setOwnerEntityId(null);
      await load();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to add ownership');
    }
  };

  const handleCreateEntityAndLink = async () => {
    if (!entityUuid) return;

    if (!ownerEntityForm.registered_name.trim()) {
      setOwnerEntityError('Registered name is required');
      return;
    }
    if (ownerEntityForm.entity_type === 'Company' && !ownerEntityForm.company_category) {
      setOwnerEntityError('Company category is required for companies');
      return;
    }
    if (!ownerPercentage || parseFloat(ownerPercentage) <= 0) {
      setOwnerEntityError('Ownership percentage is required and must be greater than 0');
      return;
    }

    setOwnerEntityError(null);
    setOwnerEntityCreating(true);
    try {
      const createRes = await api.post('/legal-entities', {
        entity_type: ownerEntityForm.entity_type,
        company_category: ownerEntityForm.entity_type === 'Company' ? ownerEntityForm.company_category : undefined,
        registered_name: ownerEntityForm.registered_name.trim(),
        registration_number: ownerEntityForm.registration_number.trim() || undefined,
        trading_name: ownerEntityForm.trading_name.trim() || undefined,
        income_tax_number: ownerEntityForm.income_tax_number.trim() || undefined,
        vat_number: ownerEntityForm.vat_number.trim() || undefined,
      });

      const newEntity = createRes.data?.data;
      if (!newEntity || !newEntity.entityId) {
        throw new Error('Failed to create entity - no entity ID returned');
      }

      await api.post('/entity-links/entity', {
        parent_entity_uuid: entityUuid,
        child_entity_id: newEntity.entityId,
        ownership_percentage: ownerPercentage,
      });

      setOwnerEntityModalOpen(false);
      setOwnerEntityForm({
        entity_type: 'Company',
        company_category: '',
        registered_name: '',
        registration_number: '',
        trading_name: '',
        income_tax_number: '',
        vat_number: '',
      });
      setOwnerPercentage('');
      await load();
    } catch (err: any) {
      console.error(err);
      setOwnerEntityError(err.response?.data?.message || 'Failed to create entity and link as owner');
    } finally {
      setOwnerEntityCreating(false);
    }
  };

  const fetchPersonOptions = async (q: string) => {
    try {
      const res = await api.get('/natural-persons', { params: { q } });
      setPersonOptions(res.data?.data ?? []);
    } catch {
      setPersonOptions([]);
    } finally {
      setPersonLoading(false);
    }
  };

  const fetchEntityOptions = async (q: string) => {
    try {
      const res = await api.get('/legal-entities', { params: { search: q } });
      setEntityOptions(res.data?.data ?? []);
    } catch {
      setEntityOptions([]);
    } finally {
      setEntityLoading(false);
    }
  };

  const handleSaveAddress = async () => {
    try {
      let addressId: number | null = null;
      if (!addressId) {
        const create = await api.post('/addresses', {
          line1: addrForm.line1 || null,
          line2: addrForm.line2 || null,
          suburb: addrForm.suburb || null,
          city: addrForm.city || null,
          postalCode: addrForm.postalCode || null,
          province: addrForm.province || null,
          country: addrForm.country || 'ZAF',
        });
        addressId = create.data?.data?.addressId;
      }
      await api.patch(`/legal-entities/${entityUuid}/addresses`, {
        registered_address_id: addrModalOpen.type === 'registered' ? addressId : undefined,
        business_address_id: addrModalOpen.type === 'business' ? addressId : undefined,
      });
      setAddrModalOpen({ type: null });
      setAddrForm({ line1: '', line2: '', suburb: '', city: '', postalCode: '', province: '', country: 'ZAF' });
      await load();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to set address');
    }
  };

  const loadGoogleMapsScript = () =>
    new Promise<void>((resolve, reject) => {
      console.debug('[GMAPS] loadGoogleMapsScript invoked');
      if ((window as any).google?.maps?.places) {
        setGmapsReady(true);
        console.debug('[GMAPS] Places already available');
        resolve();
        return;
      }
      const apiKey = GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        setGmapsUnavailable(true);
        console.warn('[GMAPS] Missing VITE_GOOGLE_MAPS_API_KEY, autocomplete disabled');
        resolve();
        return;
      }
      const existing = document.getElementById('gmaps-script');
      if (existing) {
        existing.addEventListener('load', () => {
          setGmapsReady(true);
          console.debug('[GMAPS] Existing script loaded');
          resolve();
        });
        existing.addEventListener('error', () => {
          setGmapsUnavailable(true);
          reject(new Error('Failed to load Google Maps script'));
        });
        return;
      }
      const script = document.createElement('script');
      script.id = 'gmaps-script';
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=en&region=ZA&v=weekly&loading=async`;
      script.onload = () => {
        setGmapsReady(true);
        console.debug('[GMAPS] Script loaded');
        resolve();
      };
      script.onerror = () => {
        setGmapsUnavailable(true);
        console.error('[GMAPS] Script failed to load');
        reject(new Error('Failed to load Google Maps script'));
      };
      document.head.appendChild(script);
    });

  const loadGmpxLibrary = () =>
    new Promise<void>((resolve) => {
      if (customElements.get('gmpx-place-autocomplete')) {
        setGmapsExtendedReady(true);
        resolve();
        return;
      }
      const existing = document.getElementById('gmpx-lib');
      if (existing) {
        existing.addEventListener('load', () => {
          setGmapsExtendedReady(true);
          resolve();
        });
        return;
      }
      const script = document.createElement('script');
      script.id = 'gmpx-lib';
      script.async = true;
      script.defer = true;
      script.type = 'module';
      script.src = 'https://unpkg.com/@googlemaps/extended-component-library@0.6/dist/index.min.js';
      script.onload = () => {
        setGmapsExtendedReady(true);
        resolve();
      };
      document.head.appendChild(script);
    });

  const parsePlaceToAddress = (place: any) => {
    const comps = place?.address_components || [];
    const get = (type: string) => comps.find((c: any) => c.types.includes(type));
    const streetNumber = get('street_number')?.long_name || '';
    const route = get('route')?.long_name || '';
    const line1 = [streetNumber, route].filter(Boolean).join(' ').trim() || place?.name || '';
    const suburb = get('sublocality')?.long_name || get('sublocality_level_1')?.long_name || '';
    const city = get('locality')?.long_name || get('administrative_area_level_2')?.long_name || '';
    const postalCode = get('postal_code')?.long_name || '';
    const province = get('administrative_area_level_1')?.long_name || '';
    const country = get('country')?.short_name || 'ZAF';
    return { line1, suburb, city, postalCode, province, country };
  };

  useEffect(() => {
    if (!addrModalOpen.type) return;
    let mounted = true;
    Promise.all([loadGoogleMapsScript(), loadGmpxLibrary()]).then(() => {
      if (!mounted) return;
      const hasElement = !!customElements.get('gmpx-place-autocomplete') || !!(window as any).google?.maps?.places?.PlaceAutocompleteElement;
      if (hasElement && gmapsPlaceElementRef.current) {
        try {
          (gmapsPlaceElementRef.current as any).addEventListener('gmpx-placechange', () => {
            const el: any = gmapsPlaceElementRef.current;
            const place = el?.value || el?.place || el?.getPlace?.();
            console.debug('[GMAPS] placechange (element)', place);
            if (place) {
              const parsed = parsePlaceToAddress(place);
              setAddrForm((prev) => ({
                ...prev,
                line1: parsed.line1 || prev.line1,
                suburb: parsed.suburb || prev.suburb,
                city: parsed.city || prev.city,
                postalCode: parsed.postalCode || prev.postalCode,
                province: parsed.province || prev.province,
                country: parsed.country || prev.country,
              }));
            }
          });
          setGmapsInitTried(true);
          return;
        } catch (e) {
          console.warn('[GMAPS] Failed to init PlaceAutocompleteElement, falling back', e);
        }
      }
      if ((window as any).google?.maps?.places && gmapsAutocompleteInputRef.current) {
        console.debug('[GMAPS] Initializing legacy Autocomplete on input');
        const ac = new (window as any).google.maps.places.Autocomplete(gmapsAutocompleteInputRef.current, {
          fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
          componentRestrictions: { country: ['za'] },
        });
        gmapsAutocompleteRef.current = ac;
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          console.debug('[GMAPS] place_changed (legacy)', place);
          const parsed = parsePlaceToAddress(place);
          setAddrForm((prev) => ({
            ...prev,
            line1: parsed.line1 || prev.line1,
            suburb: parsed.suburb || prev.suburb,
            city: parsed.city || prev.city,
            postalCode: parsed.postalCode || prev.postalCode,
            province: parsed.province || prev.province,
            country: parsed.country || prev.country,
          }));
        });
        setGmapsInitTried(true);
      } else {
        console.warn('[GMAPS] google.maps.places not available or inputs not mounted yet');
      }
    });
    return () => {
      mounted = false;
    };
  }, [addrModalOpen.type]);

  const tryInitAutocomplete = async () => {
    if (gmapsInitTried) return;
    await loadGoogleMapsScript();
    if ((window as any).google?.maps?.places && gmapsAutocompleteInputRef.current && !gmapsAutocompleteRef.current) {
      console.debug('[GMAPS] Lazy-initializing Autocomplete on focus');
      const ac = new (window as any).google.maps.places.Autocomplete(gmapsAutocompleteInputRef.current, {
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
        componentRestrictions: { country: ['za'] },
      });
      gmapsAutocompleteRef.current = ac;
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        console.debug('[GMAPS] place_changed fired (lazy)', place);
        const parsed = parsePlaceToAddress(place);
        setAddrForm((prev) => ({
          ...prev,
          line1: parsed.line1 || prev.line1,
          suburb: parsed.suburb || prev.suburb,
          city: parsed.city || prev.city,
          postalCode: parsed.postalCode || prev.postalCode,
          province: parsed.province || prev.province,
          country: parsed.country || prev.country,
        }));
      });
      setGmapsInitTried(true);
    }
  };

  const handleSaveDocument = async () => {
    if (!entityUuid) return;
    if (!docForm.document_type.trim()) {
      alert('Document type is required');
      return;
    }
    if (!docFile) {
      alert('Please select a file to upload');
      return;
    }
    try {
      setDocUploading(true);
      const formData = new FormData();
      formData.append('file', docFile);
      formData.append('entity_uuid', entityUuid);
      formData.append('document_type', docForm.document_type);
      if (docForm.issue_date) formData.append('issue_date', docForm.issue_date);
      if (docForm.expiry_date) formData.append('expiry_date', docForm.expiry_date);

      await api.post('/verification-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocModalOpen(false);
      setDocForm({ document_type: '', file_name: '', file_path: '', issue_date: '', expiry_date: '' });
      setDocFile(null);
      await load();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setDocUploading(false);
    }
  };

  const openUploadFor = (docType: string) => {
    setDocForm({
      document_type: docType,
      file_name: '',
      file_path: '',
      issue_date: '',
      expiry_date: '',
    });
    setDocFile(null);
    setDocModalOpen(true);
  };

  const getRequiredDocuments = useMemo(() => {
    const et = basicEdits.entity_type;
    const cat = (basicEdits.company_category ?? '').toLowerCase();

    if (et === 'Trust') {
      return { mandatory: ['Trust Deed', 'Letter of Authority'], optional: [] };
    } else {
      if (cat === 'external') {
        return { mandatory: ['CoR 20.1', 'CoR 20.2'], optional: [] };
      } else if (cat === 'cc') {
        return { mandatory: ['CK1 or CK2'], optional: [] };
      } else if (cat === 'cooperative') {
        return { mandatory: ['Co-operative Registration Certificate'], optional: [] };
      } else {
        return {
          mandatory: ['CoR 14.3', 'Test Mandatory Document'],
          optional: ['CoR 14.1', 'Test Optional Document 1', 'Test Optional Document 2'],
        };
      }
    }
  }, [basicEdits.entity_type, basicEdits.company_category]);

  const matchesRequiredDoc = (docType: string, requiredType: string): boolean => {
    const docLower = docType.toLowerCase();
    const reqLower = requiredType.toLowerCase();
    if (docLower === reqLower) return true;
    if (reqLower.includes('ck1') && docLower.includes('ck1')) return true;
    if (reqLower.includes('ck2') && docLower.includes('ck2')) return true;
    if (reqLower.includes('cor 14.1') && docLower.includes('cor 14.1')) return true;
    if (reqLower.includes('cor 14.3') && docLower.includes('cor 14.3')) return true;
    if (reqLower.includes('cor 20.1') && docLower.includes('cor 20.1')) return true;
    if (reqLower.includes('cor 20.2') && docLower.includes('cor 20.2')) return true;
    return false;
  };

  const categorizedDocuments = useMemo(() => {
    const mandatory: any[] = [];
    const optional: any[] = [];
    const additional: any[] = [];
    documents.forEach((doc: any) => {
      const docType = String(doc.documentType || '');
      const isMandatory = getRequiredDocuments.mandatory.some(req => matchesRequiredDoc(docType, req));
      const isOptional = getRequiredDocuments.optional.some(req => matchesRequiredDoc(docType, req));
      if (isMandatory) mandatory.push(doc);
      else if (isOptional) optional.push(doc);
      else additional.push(doc);
    });
    return { mandatory, optional, additional };
  }, [documents, getRequiredDocuments]);

  const getDocumentStatus = (docType: string) => {
    return documents.find((d: any) => matchesRequiredDoc(String(d.documentType || ''), docType));
  };

  const requiredDocumentsList = useMemo(() => {
    return getRequiredDocuments.mandatory.map(docType => ({
      type: docType,
      uploaded: getDocumentStatus(docType),
      isRequired: true,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, getRequiredDocuments]);

  const optionalDocumentsList = useMemo(() => {
    return getRequiredDocuments.optional.map(docType => ({
      type: docType,
      uploaded: getDocumentStatus(docType),
      isRequired: false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, getRequiredDocuments]);

  const handleViewDocument = async (documentId: number) => {
    setViewingDocId(documentId);
    setViewDocLoading(true);
    setViewDocError(null);
    setViewDocUrl(null);
    setViewDocModalOpen(true);
    try {
      const res = await api.get(`/verification-documents/${documentId}/view-url`);
      setViewDocUrl(res.data?.url || null);
    } catch (err: any) {
      console.error(err);
      setViewDocError(err.response?.data?.message || 'Failed to load document');
    } finally {
      setViewDocLoading(false);
    }
  };

  const handleSendAccessLink = async () => {
    if (!linkEmail.trim() || !entityUuid) return;
    setSendingLink(true);
    setLinkError(null);
    setLinkSent(false);
    try {
      const response = await api.post('/entity-access-tokens/send-link', {
        entity_uuid: entityUuid,
        email: linkEmail.trim(),
      });
      if (response.data.success) {
        setLinkSent(true);
        setLinkEmail('');
        setTimeout(() => {
          setSendLinkModalOpen(false);
          setLinkSent(false);
        }, 2000);
      }
    } catch (err: any) {
      setLinkError(err.response?.data?.message || 'Failed to send access link');
    } finally {
      setSendingLink(false);
    }
  };

  const handleRegenerateLink = async () => {
    if (!linkEmail.trim() || !entityUuid) return;
    setSendingLink(true);
    setLinkError(null);
    setLinkSent(false);
    try {
      const response = await api.post('/entity-access-tokens/regenerate', {
        entity_uuid: entityUuid,
        email: linkEmail.trim(),
      });
      if (response.data.success) {
        setLinkSent(true);
        setLinkEmail('');
        setTimeout(() => {
          setSendLinkModalOpen(false);
          setLinkSent(false);
        }, 2000);
      }
    } catch (err: any) {
      setLinkError(err.response?.data?.message || 'Failed to regenerate access link');
    } finally {
      setSendingLink(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/verification-documents/${documentId}`);
      await load();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to delete document');
    }
  };

  // Google Maps address search state for custom dropdown
  const [gmapsSearchText, setGmapsSearchText] = useState('');
  const [gmapsDropdownOpen, setGmapsDropdownOpen] = useState(false);
  const gmapsDropdownRef = useRef<HTMLDivElement>(null);
  const gmapsSearchDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (gmapsDropdownRef.current && !gmapsDropdownRef.current.contains(e.target as Node)) {
        setGmapsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGmapsSearch = (value: string) => {
    setGmapsSearchText(value);
    if (gmapsSearchDebounceRef.current) window.clearTimeout(gmapsSearchDebounceRef.current);
    if (!value || value.trim().length < 2 || !(window as any).google?.maps?.places) {
      setGmapsPreds([]);
      setGmapsPredLoading(false);
      setGmapsDropdownOpen(false);
      return;
    }
    setGmapsPredLoading(true);
    setGmapsDropdownOpen(true);
    gmapsSearchDebounceRef.current = window.setTimeout(() => {
      try {
        const svc = new (window as any).google.maps.places.AutocompleteService();
        const normalizedInput = value.trim();
        const words = normalizedInput.split(/\s+/).filter((w: string) => w.length > 0);
        let primaryQuery = normalizedInput;

        if (!normalizedInput.includes(',') && words.length >= 3) {
          const streetSuffixes = ['road', 'rd', 'street', 'st', 'avenue', 'ave', 'drive', 'dr', 'lane', 'ln', 'way', 'close', 'crescent', 'cres', 'boulevard', 'blvd', 'place', 'pl', 'circle', 'cir', 'court', 'ct', 'terrace', 'terr'];
          let splitIndex = -1;
          for (let i = 0; i < words.length - 1; i++) {
            if (streetSuffixes.includes(words[i].toLowerCase())) {
              splitIndex = i + 1;
              break;
            }
          }
          if (splitIndex > 0 && splitIndex < words.length) {
            primaryQuery = `${words.slice(0, splitIndex).join(' ')}, ${words.slice(splitIndex).join(' ')}`;
          } else if (words.length >= 4) {
            primaryQuery = `${words.slice(0, -2).join(' ')}, ${words.slice(-2).join(' ')}`;
          }
        }

        const searchQueries = [primaryQuery];
        if (primaryQuery !== normalizedInput) searchQueries.push(normalizedInput);
        const uniqueQueries = Array.from(new Set(searchQueries));

        const allPredictions: any[] = [];
        const seenPlaceIds = new Set<string>();
        let queryIndex = 0;

        const tryNextQuery = () => {
          if (queryIndex >= uniqueQueries.length) {
            if (allPredictions.length > 0) {
              const sorted = allPredictions.sort((a, b) => {
                const aIsAddress = a.types?.includes('street_address') || a.types?.includes('premise') || a.types?.includes('route');
                const bIsAddress = b.types?.includes('street_address') || b.types?.includes('premise') || b.types?.includes('route');
                if (aIsAddress && !bIsAddress) return -1;
                if (!aIsAddress && bIsAddress) return 1;
                return 0;
              });
              setGmapsPreds(sorted);
            } else {
              setGmapsPreds([]);
            }
            setGmapsPredLoading(false);
            return;
          }
          const query = uniqueQueries[queryIndex];
          queryIndex++;
          svc.getPlacePredictions(
            { input: query, componentRestrictions: { country: 'za' } },
            (preds: any[], status: any) => {
              if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && preds) {
                preds.forEach((pred) => {
                  if (!seenPlaceIds.has(pred.place_id)) {
                    seenPlaceIds.add(pred.place_id);
                    allPredictions.push(pred);
                  }
                });
              }
              tryNextQuery();
            },
          );
        };

        if (uniqueQueries.length === 0) {
          setGmapsPreds([]);
          setGmapsPredLoading(false);
        } else {
          tryNextQuery();
          setTimeout(() => {
            if (allPredictions.length === 0 && normalizedInput.length >= 5) {
              try {
                const textSearchService = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
                textSearchService.textSearch(
                  { query: `${normalizedInput}, South Africa`, type: 'geocode' },
                  (results: any[], status: any) => {
                    if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && results) {
                      results.forEach((result) => {
                        if (!seenPlaceIds.has(result.place_id)) {
                          seenPlaceIds.add(result.place_id);
                          allPredictions.push({
                            place_id: result.place_id,
                            description: result.formatted_address || result.name,
                            types: result.types || [],
                          });
                        }
                      });
                      if (allPredictions.length > 0) {
                        setGmapsPreds([...allPredictions]);
                        setGmapsPredLoading(false);
                      }
                    }
                  },
                );
              } catch (err) {
                console.debug('[GMAPS] Text search error:', err);
              }
            }
          }, 1000);
        }
      } catch (err) {
        console.error('[GMAPS] Error getting predictions:', err);
        setGmapsPreds([]);
        setGmapsPredLoading(false);
      }
    }, 300);
  };

  const handleGmapsSelect = (pred: any) => {
    setGmapsDropdownOpen(false);
    setGmapsSearchText(pred.description || '');
    if (!(window as any).google?.maps?.places) return;
    setAddrDetailsLoading(true);
    try {
      const mapDiv = document.createElement('div');
      const svc = new (window as any).google.maps.places.PlacesService(mapDiv);
      svc.getDetails(
        { placeId: pred.place_id, fields: ['address_components', 'formatted_address', 'name'] },
        (place: any, status: any) => {
          setAddrDetailsLoading(false);
          if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK || !place) return;
          const parsed = parsePlaceToAddress(place);
          setAddrForm((prev) => ({
            ...prev,
            line1: parsed.line1 || prev.line1,
            suburb: parsed.suburb || prev.suburb,
            city: parsed.city || prev.city,
            postalCode: parsed.postalCode || prev.postalCode,
            province: parsed.province || prev.province,
            country: parsed.country || prev.country,
          }));
        },
      );
    } catch {
      setAddrDetailsLoading(false);
    }
  };

  // Country search state for passport autocomplete
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return COUNTRIES;
    const q = countrySearch.toLowerCase();
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [countrySearch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reusable person link card renderer
  const renderPersonCard = (l: any, roleLabel: string) => {
    const isVerified = l.person?.ficaStatus === 'Verified' || l.person?.ficaStatus === 'Approved';
    return (
      <div
        key={l.entityPersonId}
        className={cn(
          'flex items-center justify-between rounded p-3',
          isVerified ? 'bg-green-500/[0.08]' : 'bg-blue-500/[0.08]',
        )}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {isVerified ? (
              <IconCircleCheck className="h-[18px] w-[18px] text-green-600" />
            ) : (
              <IconAlertCircle className="h-[18px] w-[18px] text-yellow-600" />
            )}
            <p className="text-sm font-medium">
              {l.person?.firstName} {l.person?.lastName}
              {l.ownershipPercentage && ` \u2022 ${l.ownershipPercentage}%`}
            </p>
          </div>
          <span className="ml-7 mt-1 block text-xs text-muted-foreground">
            {l.person?.email ?? 'No email'} &bull; {l.person?.phoneNumber ?? 'No phone'}
            {l.person?.idNumber && ` \u2022 ID: ${l.person.idNumber}`}
            {l.person?.passportNumber && ` \u2022 Passport: ${l.person.passportNumber} (${l.person.passportCountry})`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={cn(
              isVerified && 'border-green-200 text-green-700',
              l.person?.ficaStatus === 'Rejected' && 'border-red-200 text-red-700',
            )}
          >
            {l.person?.ficaStatus || 'Pending'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setViewingPerson(l.person);
              setViewPersonModalOpen(true);
            }}
          >
            View
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              if (confirm(`Remove ${l.person?.firstName} ${l.person?.lastName} as ${roleLabel}?`)) {
                try {
                  await api.delete(`/entity-links/person/${l.entityPersonId}`);
                  await load();
                } catch (err: any) {
                  alert(err.response?.data?.message || 'Failed to remove person');
                }
              }
            }}
          >
            <IconTrash className="mr-1 h-4 w-4" />
            Remove
          </Button>
        </div>
      </div>
    );
  };

  const openAddPersonModal = async (roleName: string, roleDisplayName: string) => {
    if (roleTypes.length === 0) {
      try {
        const rRes = await api.get('/role-types');
        const roles = rRes.data?.data ?? [];
        setRoleTypes(roles);
        if (roles.length === 0) {
          alert('Role types are being initialized. Please try again in a moment.');
          return;
        }
      } catch (err) {
        console.error('Failed to reload role types:', err);
        alert('Failed to load role types. Please refresh the page.');
        return;
      }
    }
    const role = findRoleByName(roleTypes, roleName);
    if (role) {
      setSelectedRoleId(role.roleTypeId);
      setPersonModalOpen(true);
    } else {
      const availableRoles = roleTypes.map(r => r.roleName).join(', ');
      console.error('Available roles:', availableRoles);
      alert(`Role "${roleDisplayName}" not found. Available roles: ${availableRoles || 'None'}. Please refresh the page or contact support.`);
    }
  };

  return (
    <div>
      <PageHeader
        title={entityTitle}
        subtitle="Manage entity details, addresses, related people, and ownership."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setSendLinkModalOpen(true)}>
              <IconMail className="mr-2 h-4 w-4" />
              Send Access Link
            </Button>
            <Button variant="outline" onClick={load}>
              Refresh
            </Button>
          </div>
        }
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {!loading && error && (
        <div className="p-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {!loading && !error && entity && (
        <div className="flex flex-col gap-6">
          {/* 1) Summary & Addresses */}
          <MainCard>
            <h3 className="mb-4 text-lg font-semibold">Summary</h3>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-6">
                <Label>Registered Name</Label>
                <Input readOnly value={entity.registeredName || ''} />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label>Trading Name</Label>
                <Input readOnly value={entity.tradingName || ''} />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label>Entity Type</Label>
                <Select
                  value={basicEdits.entity_type}
                  onValueChange={(val) => {
                    const nextType = val as 'Company' | 'Trust';
                    saveBasics({ entity_type: nextType, company_category: null });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Company">Company</SelectItem>
                    <SelectItem value="Trust">Trust</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label>Company Category</Label>
                <Select
                  value={basicEdits.company_category ?? ''}
                  onValueChange={(val) => saveBasics({ company_category: val || null })}
                  disabled={basicEdits.entity_type !== 'Company'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {[
                      { value: 'Pty Ltd', label: "Private Company (Pty) Ltd" },
                      { value: 'Ltd', label: "Public Company (Ltd)" },
                      { value: 'Inc', label: "Personal Liability Company (Inc)" },
                      { value: 'SOE Ltd', label: "State-Owned Company (SOE Ltd)" },
                      { value: 'NPC', label: "Non-Profit Company (NPC)" },
                      { value: 'External', label: "External Company (Foreign Branch)" },
                      { value: 'CC', label: "Close Corporation (CC)" },
                      { value: 'Cooperative', label: "Co-operative" },
                    ].map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {basicEdits.entity_type === 'Company' && (
                  <p className="mt-1 text-xs text-muted-foreground">Aligns doc types and registration format</p>
                )}
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label>Registration #</Label>
                <Input readOnly value={entity.registrationNumber || ''} />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label>VAT #</Label>
                <Input readOnly value={entity.vatNumber || ''} />
              </div>
              <div className="col-span-12 md:col-span-6">
                <Label>FICA Status</Label>
                <Input readOnly value={entity.ficaStatus || ''} />
              </div>

              {/* Addresses */}
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-6">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-sm font-medium">Registered Address</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (entity.registeredAddress) {
                            setAddrForm({
                              line1: entity.registeredAddress.line1 || '',
                              line2: entity.registeredAddress.line2 || '',
                              suburb: entity.registeredAddress.suburb || '',
                              city: entity.registeredAddress.city || '',
                              postalCode: entity.registeredAddress.postalCode || '',
                              province: entity.registeredAddress.province || '',
                              country: entity.registeredAddress.country || 'ZAF',
                            });
                          } else {
                            setAddrForm({ line1: '', line2: '', suburb: '', city: '', postalCode: '', province: '', country: 'ZAF' });
                          }
                          setAddrModalOpen({ type: 'registered' });
                        }}
                      >
                        {entity.registeredAddress ? 'Edit' : 'Add'}
                      </Button>
                    </div>
                    <MainCard className="p-3">
                      {entity.registeredAddress ? (
                        <div className="flex flex-col gap-1">
                          <p className="text-sm font-semibold">{entity.registeredAddress.line1 || '-'}</p>
                          {entity.registeredAddress.line2 && (
                            <p className="text-sm">{entity.registeredAddress.line2}</p>
                          )}
                          <p className="text-sm text-muted-foreground">
                            {[entity.registeredAddress.suburb, entity.registeredAddress.city, entity.registeredAddress.postalCode].filter(Boolean).join(', ')}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {[entity.registeredAddress.province, entity.registeredAddress.country].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No registered address captured yet.</p>
                      )}
                    </MainCard>
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-sm font-medium">Business Address</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (entity.businessAddress) {
                            setAddrForm({
                              line1: entity.businessAddress.line1 || '',
                              line2: entity.businessAddress.line2 || '',
                              suburb: entity.businessAddress.suburb || '',
                              city: entity.businessAddress.city || '',
                              postalCode: entity.businessAddress.postalCode || '',
                              province: entity.businessAddress.province || '',
                              country: entity.businessAddress.country || 'ZAF',
                            });
                          } else {
                            setAddrForm({ line1: '', line2: '', suburb: '', city: '', postalCode: '', province: '', country: 'ZAF' });
                          }
                          setAddrModalOpen({ type: 'business' });
                        }}
                      >
                        {entity.businessAddress ? 'Edit' : 'Add'}
                      </Button>
                    </div>
                    <MainCard className="p-3">
                      {entity.businessAddress ? (
                        <div className="flex flex-col gap-1">
                          <p className="text-sm font-semibold">{entity.businessAddress.line1 || '-'}</p>
                          {entity.businessAddress.line2 && (
                            <p className="text-sm">{entity.businessAddress.line2}</p>
                          )}
                          <p className="text-sm text-muted-foreground">
                            {[entity.businessAddress.suburb, entity.businessAddress.city, entity.businessAddress.postalCode].filter(Boolean).join(', ')}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {[entity.businessAddress.province, entity.businessAddress.country].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No business address captured yet.</p>
                      )}
                    </MainCard>
                  </div>
                </div>
              </div>
            </div>
          </MainCard>

          {/* Documents section */}
          <MainCard className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Documents</h3>
              <Button onClick={() => setDocModalOpen(true)}>
                <IconPlus className="mr-2 h-4 w-4" />
                Add Document
              </Button>
            </div>

            <div className="flex flex-col gap-6">
              {/* Required Documents */}
              {requiredDocumentsList.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium">Required Documents</p>
                  <MainCard className="p-3">
                    <div className="flex flex-col gap-3">
                      {requiredDocumentsList.map((item) => (
                        <div
                          key={item.type}
                          className={cn(
                            'flex items-center justify-between rounded p-3',
                            item.uploaded ? 'bg-green-500/[0.08]' : 'bg-red-500/[0.08]',
                          )}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {item.uploaded ? (
                                <IconCircleCheck className="h-[18px] w-[18px] text-green-600" />
                              ) : (
                                <IconAlertCircle className="h-[18px] w-[18px] text-red-600" />
                              )}
                              <p className="text-sm font-medium">{item.type}</p>
                            </div>
                            {item.uploaded && (
                              <span className="ml-7 mt-1 block text-xs text-muted-foreground">
                                {item.uploaded.fileName}
                                {item.uploaded.issueDate && ` \u2022 Issued: ${new Date(item.uploaded.issueDate).toLocaleDateString()}`}
                                {item.uploaded.expiryDate && ` \u2022 Expires: ${new Date(item.uploaded.expiryDate).toLocaleDateString()}`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {item.uploaded ? (
                              <>
                                <Badge variant="outline">{item.uploaded.verifiedStatus}</Badge>
                                <Button variant="outline" size="sm" onClick={() => handleViewDocument(item.uploaded.documentId)}>
                                  <IconEye className="mr-1 h-4 w-4" />
                                  View
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => openUploadFor(item.type)}>
                                  Replace
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="destructive" onClick={() => openUploadFor(item.type)}>
                                Upload
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </MainCard>
                </div>
              )}

              {/* Optional Documents */}
              {optionalDocumentsList.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium">Optional Documents</p>
                  <MainCard className="p-3">
                    <div className="flex flex-col gap-3">
                      {optionalDocumentsList.map((item) => (
                        <div
                          key={item.type}
                          className={cn(
                            'flex items-center justify-between rounded p-3',
                            item.uploaded ? 'bg-blue-500/[0.08]' : '',
                          )}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {item.uploaded ? (
                                <IconCircleCheck className="h-[18px] w-[18px] text-blue-600" />
                              ) : (
                                <div className="h-[18px] w-[18px]" />
                              )}
                              <p className="text-sm font-medium">{item.type}</p>
                            </div>
                            {item.uploaded && (
                              <span className="ml-7 mt-1 block text-xs text-muted-foreground">
                                {item.uploaded.fileName}
                                {item.uploaded.issueDate && ` \u2022 Issued: ${new Date(item.uploaded.issueDate).toLocaleDateString()}`}
                                {item.uploaded.expiryDate && ` \u2022 Expires: ${new Date(item.uploaded.expiryDate).toLocaleDateString()}`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {item.uploaded ? (
                              <>
                                <Badge variant="outline">{item.uploaded.verifiedStatus}</Badge>
                                <Button variant="outline" size="sm" onClick={() => handleViewDocument(item.uploaded.documentId)}>
                                  <IconEye className="mr-1 h-4 w-4" />
                                  View
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => openUploadFor(item.type)}>
                                  Replace
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteDocument(item.uploaded.documentId)}
                                >
                                  <IconTrash className="mr-1 h-4 w-4" />
                                  Delete
                                </Button>
                              </>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => openUploadFor(item.type)}>
                                Upload
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </MainCard>
                </div>
              )}

              {/* Additional Documents */}
              {categorizedDocuments.additional.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium">Additional Documents</p>
                  <MainCard className="p-3">
                    <div className="flex flex-col gap-3">
                      {categorizedDocuments.additional.map((d) => (
                        <div key={d.documentId} className="flex items-center justify-between rounded bg-blue-500/[0.08] p-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <IconCircleCheck className="h-[18px] w-[18px] text-blue-600" />
                              <p className="text-sm font-medium">{d.documentType}</p>
                            </div>
                            <span className="ml-7 mt-1 block text-xs text-muted-foreground">
                              {d.fileName}
                              {d.issueDate && ` \u2022 Issued: ${new Date(d.issueDate).toLocaleDateString()}`}
                              {d.expiryDate && ` \u2022 Expires: ${new Date(d.expiryDate).toLocaleDateString()}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{d.verifiedStatus}</Badge>
                            <Button variant="outline" size="sm" onClick={() => handleViewDocument(d.documentId)}>
                              <IconEye className="mr-1 h-4 w-4" />
                              View
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openUploadFor(d.documentType)}>
                              Replace
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteDocument(d.documentId)}
                            >
                              <IconTrash className="mr-1 h-4 w-4" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </MainCard>
                </div>
              )}

              {/* Empty State */}
              {documents.length === 0 && requiredDocumentsList.length === 0 && optionalDocumentsList.length === 0 && (
                <MainCard className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">No documents to display.</p>
                </MainCard>
              )}
            </div>
          </MainCard>

          {/* 2) Authorised Representatives */}
          <MainCard className="p-4">
            <h3 className="mb-2 text-lg font-semibold">Authorised Representatives</h3>
            <div className="mb-2 flex justify-end">
              <Button
                onClick={() => openAddPersonModal('AuthorisedRepresentative', 'Authorised Representative')}
                disabled={loading}
              >
                Add Representative
              </Button>
            </div>
            <div className="flex flex-col gap-3">
              {personLinks.filter(l => normalizeRoleName(l.roleType?.roleName || '') === normalizeRoleName('AuthorisedRepresentative')).length === 0 ? (
                <MainCard className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">No authorised representative linked.</p>
                </MainCard>
              ) : (
                personLinks
                  .filter((l) => normalizeRoleName(l.roleType?.roleName || '') === normalizeRoleName('AuthorisedRepresentative'))
                  .map((l) => renderPersonCard(l, 'Authorised Representative'))
              )}
            </div>
          </MainCard>

          {/* 3) Directors */}
          <MainCard className="p-4">
            <h3 className="mb-2 text-lg font-semibold">Directors</h3>
            <div className="mb-2 flex justify-end">
              <Button onClick={() => openAddPersonModal('Director', 'Director')} disabled={loading}>
                Add Director
              </Button>
            </div>
            <div className="flex flex-col gap-3">
              {personLinks.filter(l => normalizeRoleName(l.roleType?.roleName || '') === normalizeRoleName('Director')).length === 0 ? (
                <MainCard className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">No directors linked.</p>
                </MainCard>
              ) : (
                personLinks
                  .filter((l) => normalizeRoleName(l.roleType?.roleName || '') === normalizeRoleName('Director'))
                  .map((l) => renderPersonCard(l, 'Director'))
              )}
            </div>
          </MainCard>

          {/* 4) Beneficial Ownership */}
          <MainCard className="p-4">
            <h3 className="mb-2 text-lg font-semibold">Owners (5%+)</h3>
            <div className="mb-2 flex justify-end gap-2">
              <Button
                onClick={() => openAddPersonModal('BeneficialOwner_5Percent', 'Beneficial Owner')}
                disabled={loading}
              >
                Add Person
              </Button>
              <Button onClick={() => setOwnerEntityModalOpen(true)}>
                Add Entity
              </Button>
            </div>
            <p className="mb-2 text-sm font-medium">Persons</p>
            <div className="mb-4 flex flex-col gap-3">
              {personLinks.filter(l => normalizeRoleName(l.roleType?.roleName || '') === normalizeRoleName('BeneficialOwner_5Percent')).length === 0 ? (
                <MainCard className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">No person owners linked.</p>
                </MainCard>
              ) : (
                personLinks
                  .filter((l) => normalizeRoleName(l.roleType?.roleName || '') === normalizeRoleName('BeneficialOwner_5Percent'))
                  .map((l) => renderPersonCard(l, 'Beneficial Owner'))
              )}
            </div>
            <p className="mb-2 text-sm font-medium">Entities</p>
            <div className="flex flex-col gap-3">
              {entityLinks.length === 0 ? (
                <MainCard className="p-6 text-center">
                  <p className="text-sm text-muted-foreground">No entity owners linked.</p>
                </MainCard>
              ) : (
                entityLinks.map((l) => {
                  const isVerified = l.childEntity?.ficaStatus === 'Verified' || l.childEntity?.ficaStatus === 'Approved';
                  return (
                    <div
                      key={l.entityLinkId}
                      className={cn(
                        'flex items-center justify-between rounded p-3',
                        isVerified ? 'bg-green-500/[0.08]' : 'bg-blue-500/[0.08]',
                      )}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {isVerified ? (
                            <IconCircleCheck className="h-[18px] w-[18px] text-green-600" />
                          ) : (
                            <IconAlertCircle className="h-[18px] w-[18px] text-yellow-600" />
                          )}
                          <p className="text-sm font-medium">
                            {l.childEntity?.registeredName}
                            {l.ownershipPercentage && ` \u2022 ${l.ownershipPercentage}%`}
                          </p>
                        </div>
                        <span className="ml-7 mt-1 block text-xs text-muted-foreground">
                          {l.childEntity?.registrationNumber && `Reg: ${l.childEntity.registrationNumber}`}
                          {l.childEntity?.tradingName && ` \u2022 Trading: ${l.childEntity.tradingName}`}
                          {l.childEntity?.entityType && ` \u2022 Type: ${l.childEntity.entityType}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            isVerified && 'border-green-200 text-green-700',
                            l.childEntity?.ficaStatus === 'Rejected' && 'border-red-200 text-red-700',
                          )}
                        >
                          {l.childEntity?.ficaStatus || 'Pending'}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (l.childEntity?.uuid) {
                              window.location.href = `/compliance/entities/${l.childEntity.uuid}`;
                            }
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={async () => {
                            if (confirm(`Remove ${l.childEntity?.registeredName} as Beneficial Owner?`)) {
                              try {
                                await api.delete(`/entity-links/entity/${l.entityLinkId}`);
                                await load();
                              } catch (err: any) {
                                alert(err.response?.data?.message || 'Failed to remove entity');
                              }
                            }
                          }}
                        >
                          <IconTrash className="mr-1 h-4 w-4" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </MainCard>
        </div>
      )}

      {/* Add Person Modal */}
      <Dialog
        open={personModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPersonModalOpen(false);
            setPersonIdentityType('id_number');
            setPersonForm({ firstName: '', lastName: '', idNumber: '', passportNumber: '', passportCountry: 'ZAF', email: '', phoneNumber: '', dateOfBirth: '', country: 'ZAF' });
            setPhoneError('');
            setPersonFound(null);
            setShowPersonDetails(false);
            setSelectedPersonId(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Person and Link</DialogTitle>
            {selectedRoleId && roleTypes.length > 0 && roleTypes.find(r => r.roleTypeId === selectedRoleId) && (
              <DialogDescription>
                Role: {roleTypes.find(r => r.roleTypeId === selectedRoleId)?.roleName}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <p className="text-sm font-medium">Identity Information</p>

            <div>
              <Label>Identity Type</Label>
              <Select
                value={personIdentityType}
                onValueChange={(val) => {
                  setPersonIdentityType(val as 'id_number' | 'passport');
                  setPersonForm({ ...personForm, idNumber: '', passportNumber: '', passportCountry: 'ZAF', dateOfBirth: '' });
                  setPhoneError('');
                  setPersonFound(null);
                  setShowPersonDetails(false);
                  setSelectedPersonId(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="id_number">ID Number</SelectItem>
                  <SelectItem value="passport">Passport</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {personIdentityType === 'id_number' ? (
              <div>
                <Label>ID Number *</Label>
                <Input
                  value={personForm.idNumber}
                  onChange={(e) => {
                    const idNum = e.target.value;
                    const dob = extractDateOfBirthFromId(idNum);
                    setPersonForm((p) => ({ ...p, idNumber: idNum, dateOfBirth: dob || p.dateOfBirth }));
                  }}
                />
                <p className="mt-1 text-xs text-muted-foreground">Enter South African ID Number (13 digits). Date of birth will be extracted automatically.</p>
              </div>
            ) : (
              <>
                <div>
                  <Label>Passport Number *</Label>
                  <Input
                    value={personForm.passportNumber}
                    onChange={(e) => setPersonForm((p) => ({ ...p, passportNumber: e.target.value }))}
                  />
                </div>
                <div className="relative" ref={countryDropdownRef}>
                  <Label>Passport Country *</Label>
                  <Input
                    value={countrySearch || (COUNTRIES.find(c => c.code === personForm.passportCountry)?.name ?? '')}
                    onChange={(e) => {
                      setCountrySearch(e.target.value);
                      setCountryDropdownOpen(true);
                    }}
                    onFocus={() => {
                      setCountrySearch('');
                      setCountryDropdownOpen(true);
                    }}
                    placeholder="Search country..."
                  />
                  {countryDropdownOpen && filteredCountries.length > 0 && (
                    <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-md">
                      {filteredCountries.map((c) => (
                        <div
                          key={c.code}
                          className="cursor-pointer px-3 py-2 text-sm hover:bg-accent"
                          onClick={() => {
                            setPersonForm((p) => ({ ...p, passportCountry: c.code, country: c.code }));
                            setCountrySearch('');
                            setCountryDropdownOpen(false);
                          }}
                        >
                          {c.name} ({c.code})
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">Select the country that issued the passport</p>
                </div>
              </>
            )}

            <Button
              onClick={handleSearchPerson}
              disabled={personSearching || (personIdentityType === 'id_number' ? !personForm.idNumber.trim() : !personForm.passportNumber.trim() || !personForm.passportCountry)}
            >
              {personSearching && <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />}
              {personSearching ? 'Searching...' : 'Search'}
            </Button>

            {personFound === true && (
              <Alert className="border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                <AlertDescription>
                  Person found in database: <strong>{personForm.firstName} {personForm.lastName}</strong>. You can proceed to link them.
                </AlertDescription>
              </Alert>
            )}

            {personFound === false && (
              <Alert className="border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
                <AlertDescription>Person not found. Please provide additional details below.</AlertDescription>
              </Alert>
            )}

            {showPersonDetails && (
              <>
                <Separator />
                <p className="text-sm font-medium">Personal Details</p>
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={personForm.firstName}
                    onChange={(e) => setPersonForm((p) => ({ ...p, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={personForm.lastName}
                    onChange={(e) => setPersonForm((p) => ({ ...p, lastName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={personForm.dateOfBirth}
                    onChange={(e) => setPersonForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {personIdentityType === 'id_number' && personForm.idNumber && personForm.dateOfBirth ? 'Auto-extracted from ID number' : 'Enter date of birth or it will be extracted from ID number'}
                  </p>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={personForm.email}
                    onChange={(e) => setPersonForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="relative" ref={countryDropdownRef}>
                  <Label>Country</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={personForm.country}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      setPersonForm((p) => ({ ...p, country: countryCode }));
                      if (personForm.phoneNumber) {
                        const isValid = validatePhoneNumber(personForm.phoneNumber, countryCode);
                        setPhoneError(isValid ? '' : 'Invalid phone number format for selected country');
                      }
                    }}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">Select country for phone number validation</p>
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={personForm.phoneNumber}
                    onChange={(e) => {
                      const phone = e.target.value;
                      const isValid = validatePhoneNumber(phone, personForm.country);
                      setPhoneError(isValid ? '' : 'Invalid phone number format. For South Africa: +27XXXXXXXXX or 0XXXXXXXXX');
                      setPersonForm((p) => ({ ...p, phoneNumber: phone }));
                    }}
                    className={cn(phoneError && 'border-destructive')}
                  />
                  {phoneError ? (
                    <p className="mt-1 text-xs text-destructive">{phoneError}</p>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {personForm.country === 'ZAF' ? 'Format: +27XXXXXXXXX or 0XXXXXXXXX' : 'International format: +XXXXXXXXXXXXX'}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPersonModalOpen(false);
                setPersonIdentityType('id_number');
                setPersonForm({ firstName: '', lastName: '', idNumber: '', passportNumber: '', passportCountry: 'ZAF', email: '', phoneNumber: '', dateOfBirth: '', country: 'ZAF' });
                setPhoneError('');
                setPersonOwnershipPercentage('');
                setPersonFound(null);
                setShowPersonDetails(false);
                setSelectedPersonId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePersonAndLink}
              disabled={
                !selectedRoleId ||
                (personFound === false && (!personForm.firstName.trim() || !personForm.lastName.trim())) ||
                (personFound === null)
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Entity Owner Modal */}
      <Dialog
        open={ownerEntityModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setOwnerEntityModalOpen(false);
            setOwnerEntityError(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Entity Owner (5%+)</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {ownerEntityError && (
              <Alert variant="destructive">
                <AlertDescription className="flex items-center justify-between">
                  {ownerEntityError}
                  <Button variant="ghost" size="sm" className="h-auto p-1" onClick={() => setOwnerEntityError(null)}>
                    &times;
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <p className="text-sm font-medium">Entity Information</p>
            <div>
              <Label>Entity Type *</Label>
              <Select
                value={ownerEntityForm.entity_type}
                onValueChange={(val) => setOwnerEntityForm((p) => ({
                  ...p,
                  entity_type: val as 'Company' | 'Trust',
                  company_category: val === 'Trust' ? '' : p.company_category,
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Trust">Trust</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {ownerEntityForm.entity_type === 'Company' && (
              <div>
                <Label>Company Category *</Label>
                <Select
                  value={ownerEntityForm.company_category}
                  onValueChange={(val) =>
                    setOwnerEntityForm((p) => ({
                      ...p,
                      company_category: val ?? '',
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Select Category</SelectItem>
                    {[
                      { value: 'Pty Ltd', label: "Private Company (Pty) Ltd" },
                      { value: 'Ltd', label: "Public Company (Ltd)" },
                      { value: 'Inc', label: "Personal Liability Company (Inc)" },
                      { value: 'SOE Ltd', label: "State-Owned Company (SOE Ltd)" },
                      { value: 'NPC', label: "Non-Profit Company (NPC)" },
                      { value: 'External', label: "External Company (Foreign Branch)" },
                      { value: 'CC', label: "Close Corporation (CC)" },
                      { value: 'Cooperative', label: "Co-operative" },
                    ].map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">Select the company category</p>
              </div>
            )}

            <div>
              <Label>Registered Name *</Label>
              <Input
                value={ownerEntityForm.registered_name}
                onChange={(e) => setOwnerEntityForm((p) => ({ ...p, registered_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Registration Number</Label>
              <Input
                value={ownerEntityForm.registration_number}
                onChange={(e) => setOwnerEntityForm((p) => ({ ...p, registration_number: e.target.value }))}
              />
              <p className="mt-1 text-xs text-muted-foreground">Optional - can be added later</p>
            </div>
            <div>
              <Label>Trading Name</Label>
              <Input
                value={ownerEntityForm.trading_name}
                onChange={(e) => setOwnerEntityForm((p) => ({ ...p, trading_name: e.target.value }))}
              />
              <p className="mt-1 text-xs text-muted-foreground">Optional</p>
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 sm:col-span-6">
                <Label>Income Tax Number</Label>
                <Input
                  value={ownerEntityForm.income_tax_number}
                  onChange={(e) => setOwnerEntityForm((p) => ({ ...p, income_tax_number: e.target.value }))}
                />
                <p className="mt-1 text-xs text-muted-foreground">Optional</p>
              </div>
              <div className="col-span-12 sm:col-span-6">
                <Label>VAT Number</Label>
                <Input
                  value={ownerEntityForm.vat_number}
                  onChange={(e) => setOwnerEntityForm((p) => ({ ...p, vat_number: e.target.value }))}
                />
                <p className="mt-1 text-xs text-muted-foreground">Optional</p>
              </div>
            </div>

            <Separator />
            <p className="text-sm font-medium">Ownership</p>
            <div>
              <Label>Ownership Percentage *</Label>
              <Input
                type="number"
                value={ownerPercentage}
                onChange={(e) => setOwnerPercentage(e.target.value)}
                min={0}
                max={100}
                step={0.01}
              />
              <p className="mt-1 text-xs text-muted-foreground">Enter the percentage ownership (e.g., 25.5 for 25.5%)</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOwnerEntityModalOpen(false);
                setOwnerEntityError(null);
              }}
              disabled={ownerEntityCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateEntityAndLink} disabled={ownerEntityCreating}>
              {ownerEntityCreating && <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />}
              {ownerEntityCreating ? 'Creating...' : 'Create Entity & Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Address Modal */}
      <Dialog
        open={!!addrModalOpen.type}
        onOpenChange={(open) => { if (!open) setAddrModalOpen({ type: null }); }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{addrModalOpen.type === 'registered' ? 'Set Registered Address' : 'Set Business Address'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {/* Google Maps address search */}
            <div className="relative" ref={gmapsDropdownRef}>
              <Label>Search address (Google Maps)</Label>
              <Input
                value={gmapsSearchText}
                onChange={(e) => handleGmapsSearch(e.target.value)}
                onFocus={tryInitAutocomplete}
                placeholder="Start typing an address..."
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {gmapsUnavailable
                  ? 'Google Maps not configured (set VITE_GOOGLE_MAPS_API_KEY and enable Places API)'
                  : (!gmapsReady ? 'Loading Google Maps\u2026' : 'Type a full address (e.g., "21 Main Road Green Point Cape Town") for best results')}
              </p>
              {gmapsDropdownOpen && (gmapsPreds.length > 0 || gmapsPredLoading) && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-md">
                  {gmapsPredLoading && gmapsPreds.length === 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                      <IconLoader2 className="h-4 w-4 animate-spin" /> Searching...
                    </div>
                  )}
                  {gmapsPreds.map((pred) => (
                    <div
                      key={pred.place_id}
                      className="cursor-pointer px-3 py-2 text-sm hover:bg-accent"
                      onClick={() => handleGmapsSelect(pred)}
                    >
                      {pred.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {addrDetailsLoading && (
              <div className="flex items-center gap-2 py-2">
                <IconLoader2 className="h-5 w-5 animate-spin" />
                <p className="text-sm text-muted-foreground">Loading address details...</p>
              </div>
            )}
            <div>
              <Label>Line 1</Label>
              <Input value={addrForm.line1} onChange={(e) => setAddrForm((p) => ({ ...p, line1: e.target.value }))} disabled={addrDetailsLoading} />
            </div>
            <div>
              <Label>Line 2</Label>
              <Input value={addrForm.line2} onChange={(e) => setAddrForm((p) => ({ ...p, line2: e.target.value }))} disabled={addrDetailsLoading} />
            </div>
            <div>
              <Label>Suburb</Label>
              <Input value={addrForm.suburb} onChange={(e) => setAddrForm((p) => ({ ...p, suburb: e.target.value }))} disabled={addrDetailsLoading} />
            </div>
            <div>
              <Label>City</Label>
              <Input value={addrForm.city} onChange={(e) => setAddrForm((p) => ({ ...p, city: e.target.value }))} disabled={addrDetailsLoading} />
            </div>
            <div>
              <Label>Postal Code</Label>
              <Input value={addrForm.postalCode} onChange={(e) => setAddrForm((p) => ({ ...p, postalCode: e.target.value }))} disabled={addrDetailsLoading} />
            </div>
            <div>
              <Label>Province</Label>
              <Input value={addrForm.province} onChange={(e) => setAddrForm((p) => ({ ...p, province: e.target.value }))} disabled={addrDetailsLoading} />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={addrForm.country} onChange={(e) => setAddrForm((p) => ({ ...p, country: e.target.value }))} disabled={addrDetailsLoading} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddrModalOpen({ type: null })}>Cancel</Button>
            <Button onClick={handleSaveAddress}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Modal */}
      <Dialog
        open={viewDocModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setViewDocModalOpen(false);
            setViewDocUrl(null);
            setViewDocError(null);
            setViewingDocId(null);
          }
        }}
      >
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              View Document
              {viewingDocId && (
                <span className="ml-2 text-xs text-muted-foreground">Document ID: {viewingDocId}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-1 flex-col overflow-hidden p-0">
            {viewDocLoading && (
              <div className="flex flex-1 items-center justify-center">
                <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            {viewDocError && (
              <div className="p-6">
                <Alert variant="destructive">
                  <AlertDescription>{viewDocError}</AlertDescription>
                </Alert>
              </div>
            )}
            {viewDocUrl && !viewDocLoading && !viewDocError && (
              <iframe
                src={viewDocUrl}
                className="h-full w-full flex-1 border-none"
                title="Document Viewer"
              />
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setViewDocModalOpen(false);
                setViewDocUrl(null);
                setViewDocError(null);
                setViewingDocId(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Person Modal */}
      <Dialog
        open={viewPersonModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setViewPersonModalOpen(false);
            setViewingPerson(null);
            setVerificationHistory([]);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Person Details</DialogTitle>
            {viewingPerson && (
              <DialogDescription>
                {viewingPerson.firstName} {viewingPerson.lastName}
              </DialogDescription>
            )}
          </DialogHeader>

          {viewingPerson ? (
            <div className="flex flex-col gap-4">
              <Separator />
              <p className="text-sm font-medium">Personal Information</p>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 sm:col-span-6">
                  <Label>First Name</Label>
                  <Input readOnly value={viewingPerson.firstName || ''} />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Last Name</Label>
                  <Input readOnly value={viewingPerson.lastName || ''} />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Date of Birth</Label>
                  <Input readOnly value={viewingPerson.dateOfBirth ? new Date(viewingPerson.dateOfBirth).toLocaleDateString() : ''} />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Email</Label>
                  <Input readOnly value={viewingPerson.email || ''} />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Phone Number</Label>
                  <Input readOnly value={viewingPerson.phoneNumber || ''} />
                </div>
              </div>

              <Separator />
              <p className="text-sm font-medium">Identity Information</p>
              <div className="grid grid-cols-12 gap-4">
                {viewingPerson.idNumber && (
                  <div className="col-span-12 sm:col-span-6">
                    <Label>ID Number</Label>
                    <Input readOnly value={viewingPerson.idNumber} />
                  </div>
                )}
                {viewingPerson.passportNumber && (
                  <>
                    <div className="col-span-12 sm:col-span-6">
                      <Label>Passport Number</Label>
                      <Input readOnly value={viewingPerson.passportNumber} />
                    </div>
                    <div className="col-span-12 sm:col-span-6">
                      <Label>Passport Country</Label>
                      <Input readOnly value={viewingPerson.passportCountry ? COUNTRIES.find(c => c.code === viewingPerson.passportCountry)?.name || viewingPerson.passportCountry : ''} />
                    </div>
                  </>
                )}
              </div>

              {viewingPerson.residentialAddress && (
                <>
                  <Separator />
                  <p className="text-sm font-medium">Residential Address</p>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12">
                      <Label>Address Line 1</Label>
                      <Input readOnly value={viewingPerson.residentialAddress.line1 || ''} />
                    </div>
                    {viewingPerson.residentialAddress.line2 && (
                      <div className="col-span-12">
                        <Label>Address Line 2</Label>
                        <Input readOnly value={viewingPerson.residentialAddress.line2} />
                      </div>
                    )}
                    <div className="col-span-12 sm:col-span-6">
                      <Label>Suburb</Label>
                      <Input readOnly value={viewingPerson.residentialAddress.suburb || ''} />
                    </div>
                    <div className="col-span-12 sm:col-span-6">
                      <Label>City</Label>
                      <Input readOnly value={viewingPerson.residentialAddress.city || ''} />
                    </div>
                    <div className="col-span-12 sm:col-span-4">
                      <Label>Postal Code</Label>
                      <Input readOnly value={viewingPerson.residentialAddress.postalCode || ''} />
                    </div>
                    <div className="col-span-12 sm:col-span-4">
                      <Label>Province</Label>
                      <Input readOnly value={viewingPerson.residentialAddress.province || ''} />
                    </div>
                    <div className="col-span-12 sm:col-span-4">
                      <Label>Country</Label>
                      <Input readOnly value={viewingPerson.residentialAddress.country ? COUNTRIES.find(c => c.code === viewingPerson.residentialAddress.country)?.name || viewingPerson.residentialAddress.country : ''} />
                    </div>
                  </div>
                </>
              )}

              <Separator />
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Verification Status</p>
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 sm:col-span-6">
                  <Label>FICA Status</Label>
                  <Input readOnly value={viewingPerson.ficaStatus || 'Pending'} />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Last Verification Date</Label>
                  <Input readOnly value={viewingPerson.lastVerificationDate ? new Date(viewingPerson.lastVerificationDate).toLocaleDateString() : 'Never'} />
                </div>
              </div>

              {/* Verification History */}
              {verificationHistory.length > 0 && (
                <>
                  <Separator />
                  <p className="text-sm font-medium">Verification History</p>
                  <div className="flex flex-col gap-2">
                    {verificationHistory.map((v: any) => (
                      <MainCard key={v.verificationId} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Badge
                              variant="outline"
                              className={cn(
                                v.verificationStatus === 'Matched' && 'border-green-200 text-green-700',
                                v.verificationStatus === 'Mismatch' && 'border-red-200 text-red-700',
                              )}
                            >
                              {v.verificationStatus}
                            </Badge>
                            <span className="mt-1 block text-xs text-muted-foreground">
                              {new Date(v.createdAt).toLocaleString()} &bull; Method: {v.verificationMethod}
                              {v.verifiedBy && ` \u2022 By: ${v.verifiedBy}`}
                            </span>
                          </div>
                        </div>
                      </MainCard>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="text-sm">No person data available</p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setViewPersonModalOpen(false);
                setViewingPerson(null);
                setVerificationHistory([]);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setImageDialogOpen(false);
            setSelectedImageUrl(null);
            setSelectedImageTitle('');
          }
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{selectedImageTitle}</DialogTitle>
          </DialogHeader>
          {selectedImageUrl && (
            <img
              src={selectedImageUrl}
              alt={selectedImageTitle}
              className="h-auto w-full object-contain"
            />
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setImageDialogOpen(false);
                setSelectedImageUrl(null);
                setSelectedImageTitle('');
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Modal */}
      <Dialog open={docModalOpen} onOpenChange={(open) => { if (!open) setDocModalOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <Label>Document Type *</Label>
              <Input
                value={docForm.document_type}
                onChange={(e) => setDocForm((p) => ({ ...p, document_type: e.target.value }))}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {docForm.document_type ? 'Pre-filled from required documents list' : 'Enter document type or select from required documents'}
              </p>
            </div>
            <div>
              <input
                accept=".pdf,application/pdf"
                style={{ display: 'none' }}
                id="document-file-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                      alert('Only PDF files are allowed');
                      e.target.value = '';
                      return;
                    }
                    setDocFile(file);
                    if (docForm.document_type && !docForm.file_name) {
                      const sanitizedType = docForm.document_type.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                      setDocForm((p) => ({ ...p, file_name: `${sanitizedType}.pdf` }));
                    } else if (!docForm.file_name) {
                      setDocForm((p) => ({ ...p, file_name: file.name }));
                    }
                  }
                }}
              />
              <label
                htmlFor="document-file-upload"
                className={cn(buttonVariants({ variant: 'outline' }), 'w-full cursor-pointer')}
              >
                {docFile ? `Selected: ${docFile.name}` : 'Choose File to Upload'}
              </label>
              {docFile && (
                <span className="mt-1 block text-xs text-muted-foreground">
                  File size: {(docFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              )}
            </div>
            <div>
              <Label>File Name (optional, auto-filled from file)</Label>
              <Input
                value={docForm.file_name}
                onChange={(e) => setDocForm((p) => ({ ...p, file_name: e.target.value }))}
              />
              <p className="mt-1 text-xs text-muted-foreground">Will be used as the filename in storage.</p>
            </div>
            <div>
              <Label>Issue Date</Label>
              <Input
                type="date"
                value={docForm.issue_date}
                onChange={(e) => setDocForm((p) => ({ ...p, issue_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={docForm.expiry_date}
                onChange={(e) => setDocForm((p) => ({ ...p, expiry_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDocModalOpen(false);
                setDocFile(null);
                setDocForm({ document_type: '', file_name: '', file_path: '', issue_date: '', expiry_date: '' });
              }}
              disabled={docUploading}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveDocument} disabled={docUploading || !docFile || !docForm.document_type.trim()}>
              {docUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Access Link Dialog */}
      <Dialog open={sendLinkModalOpen} onOpenChange={(open) => { if (!open && !sendingLink) setSendLinkModalOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Access Link to Entity</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Send a secure access link to the entity contact. The link will expire in 7 days and allows them to complete entity details without requiring a Google account.
            </p>

            {linkSent && (
              <Alert className="border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                <IconCircleCheck className="h-4 w-4" />
                <AlertDescription>Access link sent successfully!</AlertDescription>
              </Alert>
            )}

            {linkError && (
              <Alert variant="destructive">
                <AlertDescription className="flex items-center justify-between">
                  {linkError}
                  <Button variant="ghost" size="sm" className="h-auto p-1" onClick={() => setLinkError(null)}>
                    &times;
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                placeholder="entity@example.com"
                disabled={sendingLink}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> If an active link already exists, it will be invalidated and a new one will be created.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendLinkModalOpen(false)} disabled={sendingLink}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleRegenerateLink}
              disabled={sendingLink || !linkEmail.trim()}
            >
              Regenerate & Send
            </Button>
            <Button onClick={handleSendAccessLink} disabled={sendingLink || !linkEmail.trim()}>
              {sendingLink ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {sendingLink ? 'Sending...' : 'Send Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
